import React, { useEffect, useState } from 'react';
import TwAutocomplete, {
  RenderOption,
  TwAutocompleteOption
} from '../TwAutocomplete/TwAutocomplete';
import { SxProps } from '@mui/material';
import useAuth from '../../providers/Auth/Auth.provider';
import { USER_ROLE } from '../../providers/User/User.model';
import {
  useSearchOtherExpenseFieldQuery,
  useSearchOtherExpensesQuery
} from '../../redux/api/otherExpenses/otherExpenseApi';
import { SearchOtherExpenseResponse } from '../../redux/api/otherExpenses/otherExpense.model';
import CreateOrEditOtheExpenseForm from '../../pages/OtherExpenses/CreateOrEditExpense/CreateOrEditExpense';

type ExpenseSelectorProps = {
  label?: string;
  selectedExpense: TwAutocompleteOption | null | undefined;
  hasError: boolean;
  onChange: (value: string) => void;
  setSelectedExpense: (expense: TwAutocompleteOption | null) => void;
  disabled?: boolean;
  readOnly?: boolean;
  sx?: SxProps;
  fromDocManager?: boolean;
  setOpenExpenseModal?: (open: boolean) => void;
  noActions?: boolean;
};

const TwExpenseSelector: React.FC<ExpenseSelectorProps> = ({
  label,
  selectedExpense,
  hasError,
  setSelectedExpense,
  onChange,
  disabled = false,
  readOnly = false,
  sx = {},
  fromDocManager = false,
  setOpenExpenseModal,
  noActions = false
}): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const [expenseOptions, setExpenseOptions] = React.useState<TwAutocompleteOption[]>([]);
  const [searchExpense, setSearchExpense] = useState<string>('');
  const [blockFetchExpenses, setBlockFetchExpenses] = useState<boolean>(false);
  const [openCreateExpenseModal, setOpenCreateExpenseModal] = useState<boolean>(false);

  // Fetch expense list
  const { data: expenseList = { content: [], total: 0 } } = useSearchOtherExpensesQuery(
    {
      params: {
        search: searchExpense,
        limit: 5,
        offset: 0
      }
    },
    {
      skip: blockFetchExpenses || readOnly || searchExpense !== ''
    }
  );

  const { data: expenseListByName = { content: [], total: 0 } } = useSearchOtherExpenseFieldQuery(
    {
      field: 'name',
      search: searchExpense
    },
    {
      skip: blockFetchExpenses || readOnly || searchExpense === ''
    }
  );

  // Transform other expense list
  const transformExpenseList = (expenses: SearchOtherExpenseResponse[]): TwAutocompleteOption[] => {
    return expenses.map((expense) => ({
      id: expense.id,
      value: expense.id,
      label: expense.name
    }));
  };

  const transformExpenseListByName = (
    expenses: SearchOtherExpenseResponse[]
  ): TwAutocompleteOption[] => {
    return expenses.map((expense) => ({
      id: expense.id,
      value: expense.id,
      label: expense.name
    }));
  };

  // Open create expense modal
  const handleOpenCreateExpense = (): void => {
    setOpenExpenseModal && setOpenExpenseModal(true);
    setOpenCreateExpenseModal(true);
  };

  // Close create expense modal
  const handleCloseCreateExpense = (): void => {
    setOpenExpenseModal && setOpenExpenseModal(false);
    setOpenCreateExpenseModal(false);
  };

  // Create expense success
  const handleCreateExpenseSuccess = (newExpense: SearchOtherExpenseResponse): void => {
    setSelectedExpense({
      id: newExpense.id,
      value: newExpense.id,
      label: newExpense.name
    });

    onChange(newExpense.id);
  };

  useEffect(() => {
    if (expenseList.content.length > 0) {
      setBlockFetchExpenses(true);
      setExpenseOptions(transformExpenseList(expenseList.content));
    }
  }, [JSON.stringify(expenseList.content)]);

  useEffect(() => {
    if (expenseListByName.content.length > 0) {
      setBlockFetchExpenses(true);
      setExpenseOptions(transformExpenseListByName(expenseListByName.content));
    }
  }, [JSON.stringify(expenseListByName.content)]);

  return (
    <>
      <TwAutocomplete
        label={label}
        name="expense"
        filterOptions={(x: any): any => x}
        renderOption={(props, option): React.ReactNode => {
          return <RenderOption key={option.id} props={props} option={option} />;
        }}
        optionList={expenseOptions}
        value={selectedExpense || null}
        onChange={(_, newValue: TwAutocompleteOption | null): void => {
          const newExpense = newValue ? ({ ...newValue } as TwAutocompleteOption) : null;
          setSelectedExpense(newExpense);
          onChange(newValue?.id || '');
        }}
        onInputChange={(_, newInputValue): void => {
          setSearchExpense(newInputValue || '');
          setBlockFetchExpenses(false);
        }}
        listAction={
          (userData?.role !== USER_ROLE.DRIVER && !readOnly) || !noActions
            ? {
                label: 'New expense',
                action: handleOpenCreateExpense
              }
            : undefined
        }
        error={hasError}
        readonly={readOnly}
        showEndAdornment={!readOnly}
        disabled={disabled}
        sx={sx}
      />

      {openCreateExpenseModal && (
        <CreateOrEditOtheExpenseForm
          otherExpenseId={null}
          open={openCreateExpenseModal}
          onClose={handleCloseCreateExpense}
          onSuccess={handleCreateExpenseSuccess}
          fromDocManager={fromDocManager}
        />
      )}
    </>
  );
};

export default TwExpenseSelector;
